import React, { useMemo, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Tooltip } from 'antd';
import { FaInstagram, FaUsers, FaInbox, FaChevronRight } from 'react-icons/fa';
import moment from 'moment';
import { loadCadenceSummary } from '../actions';
import {
  Container,
  BannerList,
  Banner,
  BannerLeft,
  BannerIcon,
  BannerInfo,
  BannerTitle,
  BannerSubtitle,
  BannerRight,
  MetricsGroup,
  Metric,
  MetricValue,
  MetricLabel,
  ConversionTag,
  ArrowIcon,
  SkeletonBanner,
  SkeletonLeft,
  SkeletonIcon,
  SkeletonText,
  SkeletonTitle,
  SkeletonSubtitle,
  SkeletonRight,
  SkeletonMetricsGroup,
  SkeletonMetric,
  SkeletonMetricValue,
  SkeletonMetricLabel,
  SkeletonConversionTag,
  SkeletonArrow,
  EmptyState,
  EmptyIcon,
  EmptyTitle,
  EmptyDescription
} from '~/_shared/components/CadenceSelection/styles';

// Format time ago from date string
var formatTimeAgo = function(dateString) {
  if (!dateString) return null;
  return moment(dateString).fromNow();
};

// Get icon based on cadence name
var getIconForCadence = function(cadenceName) {
  var nameLower = cadenceName.toLowerCase();
  if (nameLower.indexOf('instagram') !== -1) {
    return FaInstagram;
  }
  return FaUsers;
};

// Get color based on cadence name - using app's primary purple and muted colors
var getColorForCadence = function(cadenceName, index) {
  var colors = ['#722ed1', '#1890ff', '#13c2c2', '#52c41a', '#faad14', '#eb2f96'];
  var nameLower = cadenceName.toLowerCase();

  if (nameLower.indexOf('médico') !== -1 || nameLower.indexOf('medico') !== -1) {
    return '#722ed1';
  }
  if (nameLower.indexOf('contador') !== -1) {
    return '#1890ff';
  }
  if (nameLower.indexOf('neutro') !== -1) {
    return '#13c2c2';
  }

  return colors[index % colors.length];
};

// Skeleton loading component
var BannerSkeleton = function(props) {
  var index = props.index || 0;
  return (
    <SkeletonBanner index={index}>
      <SkeletonLeft>
        <SkeletonIcon />
        <SkeletonText>
          <SkeletonTitle />
          <SkeletonSubtitle />
        </SkeletonText>
      </SkeletonLeft>
      <SkeletonRight>
        <SkeletonMetricsGroup>
          <SkeletonMetric>
            <SkeletonMetricValue />
            <SkeletonMetricLabel />
          </SkeletonMetric>
          <SkeletonMetric>
            <SkeletonMetricValue />
            <SkeletonMetricLabel />
          </SkeletonMetric>
          <SkeletonMetric>
            <SkeletonMetricValue />
            <SkeletonMetricLabel />
          </SkeletonMetric>
        </SkeletonMetricsGroup>
        <SkeletonConversionTag />
        <SkeletonArrow />
      </SkeletonRight>
    </SkeletonBanner>
  );
};

var CadenceSelection = function(props) {
  var onSelect = props.onSelect;
  var dispatch = useDispatch();
  var cadenceSummary = useSelector(function(state) { return state.socialSelling.cadenceSummary; });
  var allCadences = useSelector(function(state) { return state.settings['socialSelling.cadence']; });

  // Load cadence summary on mount
  useEffect(function() {
    dispatch(loadCadenceSummary());
  }, [dispatch]);

  // Parse cadences from settings to get id and steps count
  var parsedCadences = useMemo(function() {
    if (!allCadences) return [];

    return allCadences.map(function(cadence) {
      try {
        var parsedValue = JSON.parse(cadence.value);
        return {
          id: cadence.id,
          name: parsedValue.name,
          data: parsedValue.data || []
        };
      } catch (e) {
        console.error('Error parsing cadence:', e);
        return null;
      }
    }).filter(Boolean);
  }, [allCadences]);

  // Merge cadence settings with summary data from API
  var cadenceMetrics = useMemo(function() {
    var summaryData = cadenceSummary && cadenceSummary.data ? cadenceSummary.data : [];

    return parsedCadences.map(function(cadence, index) {
      // Find matching summary data by name
      var summary = summaryData.find(function(s) { return s.name === cadence.name; });

      var totalLeads = summary ? summary.totalLeads : 0;
      var positivo = summary ? summary.positivo : 0;
      var emAndamento = summary ? summary.emAndamento : 0;
      var conversionRate = summary ? summary.conversionRate : 0;
      var lastUpdatedAt = summary ? summary.lastUpdatedAt : null;
      var stepsCount = cadence.data ? cadence.data.length : 0;

      return {
        id: cadence.id,
        name: cadence.name,
        data: cadence.data,
        totalLeads: totalLeads,
        positivo: positivo,
        emAndamento: emAndamento,
        conversionRate: conversionRate,
        lastUpdatedAt: lastUpdatedAt,
        stepsCount: stepsCount,
        color: getColorForCadence(cadence.name, index),
        Icon: getIconForCadence(cadence.name)
      };
    });
  }, [parsedCadences, cadenceSummary]);

  var handleBannerClick = function(cadenceId) {
    if (onSelect) {
      onSelect(cadenceId);
    }
  };

  // Loading state with skeleton
  // Show skeleton when loading OR when summary data hasn't been fetched yet
  var isLoading = cadenceSummary && cadenceSummary.isLoading;
  var summaryData = cadenceSummary && cadenceSummary.data ? cadenceSummary.data : [];
  var hasLoadedSummaryData = summaryData.length > 0;

  if (isLoading || !hasLoadedSummaryData) {
    return (
      <Container>
        <BannerList>
          <BannerSkeleton index={0} />
          <BannerSkeleton index={1} />
          <BannerSkeleton index={2} />
        </BannerList>
      </Container>
    );
  }

  // Empty state
  if (parsedCadences.length === 0) {
    return (
      <Container>
        <EmptyState>
          <EmptyIcon>
            <FaInbox />
          </EmptyIcon>
          <EmptyTitle>Nenhuma cadência configurada</EmptyTitle>
          <EmptyDescription>
            Configure suas cadências nas configurações do sistema para começar a gerenciar leads.
          </EmptyDescription>
        </EmptyState>
      </Container>
    );
  }

  return (
    <Container>
      <BannerList>
        {cadenceMetrics.map(function(cadence, index) {
          var IconComponent = cadence.Icon;
          return (
            <Banner
              key={cadence.id}
              index={index}
              onClick={function() { handleBannerClick(cadence.id); }}
            >
              <BannerLeft>
                <BannerIcon color={cadence.color}>
                  <IconComponent />
                </BannerIcon>
                <BannerInfo>
                  <BannerTitle>{cadence.name}</BannerTitle>
                  <BannerSubtitle>
                    {cadence.stepsCount} {cadence.stepsCount === 1 ? 'etapa' : 'etapas'}
                    {cadence.lastUpdatedAt && (
                      <span> · Atualizado {formatTimeAgo(cadence.lastUpdatedAt)}</span>
                    )}
                  </BannerSubtitle>
                </BannerInfo>
              </BannerLeft>
              <BannerRight>
                <MetricsGroup>
                  <Tooltip title="Total de leads nesta cadência (todos os períodos)">
                    <Metric>
                      <MetricValue>{cadence.totalLeads}</MetricValue>
                      <MetricLabel>Total</MetricLabel>
                    </Metric>
                  </Tooltip>
                  <Tooltip title="Leads em andamento - ainda não finalizados (todos os períodos)">
                    <Metric>
                      <MetricValue color="#1890ff">{cadence.emAndamento}</MetricValue>
                      <MetricLabel>Andamento</MetricLabel>
                    </Metric>
                  </Tooltip>
                  <Tooltip title="Leads com resultado positivo (todos os períodos)">
                    <Metric>
                      <MetricValue color="#52c41a">{cadence.positivo}</MetricValue>
                      <MetricLabel>Positivo</MetricLabel>
                    </Metric>
                  </Tooltip>
                </MetricsGroup>
                <Tooltip title="Taxa de conversão = Positivos / Total (todos os períodos)">
                  <ConversionTag rate={cadence.conversionRate}>
                    {cadence.conversionRate}% conversão
                  </ConversionTag>
                </Tooltip>
                <ArrowIcon>
                  <FaChevronRight />
                </ArrowIcon>
              </BannerRight>
            </Banner>
          );
        })}
      </BannerList>
    </Container>
  );
};

export default React.memo(CadenceSelection);
